import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StaffLink } from '../components/EntityLinks';
import { Search, UserCircle, ChevronRight, ChevronLeft, Loader2, Plus, Mars, Venus, Trash2, CheckCircle2, ChevronDown, ShieldOff, ShieldCheck, X, Landmark, Eye, EyeOff, ArrowDown, ArrowUpDown, Home, Hospital } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import FilterableTh from '../components/FilterableTh';
import apiClient from '../../../api/api';
import useAutoRefresh from '../../../hooks/useAutoRefresh';
import useDebouncedValue from '../../../hooks/useDebouncedValue';
import { formatMobileNumber, formatMobileNumbers } from '../../../utils/phoneFormat';

const PAGE_SIZE = 50;

const ROLE_LABELS = {
  CARETAKER: 'Caretaker',
  NURSING_ASSISTANT: 'Nursing Assistant',
  NURSE: 'Nurse',
  PHYSIOTHERAPIST: 'Physiotherapist',
  NANNY: 'Nanny',
  COUNSELLOR: 'Counsellor',
};

const STATUS_TABS = ['All', 'available', 'unavailable', 'assigned', 'on_leave'];

const TAB_LABELS = {
  All: 'All',
  available: 'Available',
  unavailable: 'Unavailable',
  assigned: 'On Assignment',
  on_leave: 'On Leave',
};

function parseRoles(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  return raw.replace(/^\{|\}$/g, '').split(',').map(r => r.trim()).filter(Boolean);
}

function formatRoles(raw) {
  return parseRoles(raw).map(r => ROLE_LABELS[r] || r.replace(/_/g, ' ')).join(', ') || '—';
}

const StatusDot = ({ status }) => {
  const cfg = {
    available:   { dot: 'bg-emerald-500', text: 'text-emerald-700', label: 'Available' },
    unavailable: { dot: 'bg-slate-400',   text: 'text-slate-500',   label: 'Unavailable' },
    assigned:    { dot: 'bg-blue-500',    text: 'text-blue-700',    label: 'On Assignment' },
    on_leave:    { dot: 'bg-amber-400',   text: 'text-amber-700',   label: 'On Leave' },
  }[status?.toLowerCase()] ?? { dot: 'bg-slate-300', text: 'text-slate-500', label: status ?? '—' };

  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const GenderIcon = ({ gender }) => {
  const cfg = {
    MALE:   { Icon: Mars,  className: 'text-blue-500',   label: 'Male' },
    FEMALE: { Icon: Venus, className: 'text-pink-500',   label: 'Female' },
  }[gender?.toUpperCase()];

  if (!cfg) return null;

  const { Icon, className, label } = cfg;
  return <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${className}`} aria-label={label} title={label} />;
};

const DocsStatusDot = ({ worker }) => {
  const submitted = !!(worker.grama_niladhari_url && worker.police_report_url);
  const cfg = submitted
    ? { dot: 'bg-emerald-500', text: 'text-emerald-700', label: 'Documents Submitted' }
    : { dot: 'bg-amber-400',   text: 'text-amber-700',   label: 'Documents Pending' };

  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
};

const StaffManagement = () => {
  const navigate = useNavigate();
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('All');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 400);
  const [pendingMigrationOnly, setPendingMigrationOnly] = useState(false);
  const [portalFilter, setPortalFilter] = useState('all'); // 'all' | 'enabled' | 'disabled'
  const [liveInFilter, setLiveInFilter] = useState('all'); // 'all' | 'yes' | 'no'
  const [hospitalFilter, setHospitalFilter] = useState('all'); // 'all' | 'yes' | 'no'
  const [sortEarningsDesc, setSortEarningsDesc] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [counts, setCounts] = useState({ All: 0 });

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);
  const [selectAllLoading, setSelectAllLoading] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsRef = useRef(null);
  const [portalConfirm, setPortalConfirm] = useState(null); // 'disable' | 'enable' | null
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState(null);
  const [publicConfirm, setPublicConfirm] = useState(null); // 'show' | 'hide' | null
  const [publicLoading, setPublicLoading] = useState(false);
  const [publicError, setPublicError] = useState(null);

  // Close the Actions dropdown on outside click / Escape.
  useEffect(() => {
    if (!actionsOpen) return undefined;
    const onDown = (e) => { if (actionsRef.current && !actionsRef.current.contains(e.target)) setActionsOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setActionsOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [actionsOpen]);

  // Reset back to page 1 whenever a filter/search changes underneath the current page.
  useEffect(() => { setPage(1); }, [activeTab, debouncedSearch, pendingMigrationOnly, portalFilter, liveInFilter, hospitalFilter, sortEarningsDesc]);

  useEffect(() => { fetchWorkers(); }, [activeTab, debouncedSearch, pendingMigrationOnly, portalFilter, liveInFilter, hospitalFilter, sortEarningsDesc, page]);

  // Clear selection whenever the underlying list changes to avoid acting on stale rows.
  useEffect(() => { setSelectedIds(new Set()); }, [activeTab, debouncedSearch, pendingMigrationOnly, portalFilter, liveInFilter, hospitalFilter, sortEarningsDesc, page]);

  const buildFilters = () => ({
    page,
    limit: PAGE_SIZE,
    ...(activeTab !== 'All' ? { status: activeTab } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(pendingMigrationOnly ? { pending_migration: 'true' } : {}),
    ...(portalFilter !== 'all' ? { portal_access: portalFilter } : {}),
    ...(liveInFilter !== 'all' ? { live_in: liveInFilter } : {}),
    ...(hospitalFilter !== 'all' ? { hospital: hospitalFilter } : {}),
    ...(sortEarningsDesc ? { sort_earnings: 'desc' } : {}),
  });

  const fetchWorkers = async ({ silent = false } = {}) => {
    // Only take over the whole page with the spinner on the very first load.
    // Later fetches (search/tab/page changes, auto-refresh) update in place so
    // the search input never unmounts and loses focus.
    const showSpinner = !silent && !hasLoadedOnce;
    try {
      if (showSpinner) setLoading(true);
      if (!silent) setError(null);
      const response = await apiClient.getAllStaff(buildFilters());
      setWorkers(response.data || []);
      setPagination(response.pagination || null);
      if (response.counts) setCounts(response.counts);
      setHasLoadedOnce(true);
    } catch (err) {
      if (!silent) setError('Failed to load staff');
      else console.error('StaffManagement silent refresh error:', err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  useAutoRefresh(() => fetchWorkers({ silent: true }), { intervalMs: 5000 });

  const toggleSelectMode = () => {
    setSelectMode(v => !v);
    setSelectedIds(new Set());
  };

  const toggleSelect = (staffId) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(staffId)) next.delete(staffId);
      else next.add(staffId);
      return next;
    });
  };

  const toggleSelectAll = async () => {
    const totalCount = pagination?.total_count ?? workers.length;
    const allSelected = totalCount > 0 && selectedIds.size === totalCount;

    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }

    // Everything matching the current filters is already on this single page.
    if (totalCount <= workers.length) {
      setSelectedIds(new Set(workers.map(w => w.staff_profile_id)));
      return;
    }

    // Otherwise fetch every staff member matching the current filters (not just this page)
    // so "select all" covers the whole filtered result set, not one page of it.
    setSelectAllLoading(true);
    try {
      const response = await apiClient.getAllStaff({ ...buildFilters(), page: 1, limit: totalCount });
      const ids = (response.data || []).map(w => w.staff_profile_id);
      setSelectedIds(new Set(ids));
    } catch (err) {
      console.error('Failed to select all matching staff:', err);
    } finally {
      setSelectAllLoading(false);
    }
  };

  const openDeleteConfirm = () => {
    setDeleteConfirmText('');
    setDeleteError(null);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    if (deleteLoading) return;
    setShowDeleteConfirm(false);
    setDeleteConfirmText('');
    setDeleteError(null);
  };

  const handleDeleteConfirmed = async () => {
    if (deleteConfirmText.trim().toLowerCase() !== 'confirm') return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const ids = Array.from(selectedIds);
      const results = await Promise.allSettled(ids.map(id => apiClient.deleteStaffProfile(id)));
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length > 0) {
        setDeleteError(
          failed.length === ids.length
            ? (failed[0].reason?.message || 'Failed to delete selected staff member(s).')
            : `${failed.length} of ${ids.length} staff member(s) could not be deleted. They may have active bookings.`
        );
      }
      setSelectedIds(new Set());
      await fetchWorkers();
      if (failed.length === 0) {
        setShowDeleteConfirm(false);
        setDeleteConfirmText('');
      }
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete selected staff member(s).');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleMarkSelectedAvailable = async () => {
    setAvailabilityLoading(true);
    setAvailabilityError(null);
    try {
      const ids = Array.from(selectedIds);
      const results = await Promise.allSettled(
        ids.map(id => apiClient.updateStaffStatus(id, 'AVAILABLE'))
      );
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length > 0) {
        setAvailabilityError(
          failed.length === ids.length
            ? (failed[0].reason?.message || 'Failed to update selected staff member(s).')
            : `${failed.length} of ${ids.length} staff member(s) could not be updated.`
        );
      }
      setSelectedIds(new Set());
      await fetchWorkers();
    } catch (err) {
      setAvailabilityError(err.message || 'Failed to update selected staff member(s).');
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handlePortalAccessConfirmed = async () => {
    const disabled = portalConfirm === 'disable';
    setPortalLoading(true);
    setPortalError(null);
    try {
      const ids = Array.from(selectedIds);
      await apiClient.bulkSetStaffPortalAccess(ids, disabled);
      setSelectedIds(new Set());
      setPortalConfirm(null);
      await fetchWorkers();
    } catch (err) {
      setPortalError(err.message || 'Failed to update dashboard access for the selected staff.');
    } finally {
      setPortalLoading(false);
    }
  };

  const handlePublicVisibilityConfirmed = async () => {
    setPublicLoading(true);
    setPublicError(null);
    try {
      await apiClient.bulkSetStaffPublicVisibility(Array.from(selectedIds), publicConfirm === 'show');
      setSelectedIds(new Set());
      setPublicConfirm(null);
      await fetchWorkers();
    } catch (err) {
      setPublicError(err.message || 'Failed to update public visibility for the selected staff.');
    } finally {
      setPublicLoading(false);
    }
  };

  const runAction = (fn) => { setActionsOpen(false); fn(); };

  const pendingMigrationCount = counts.pending_migration || 0;
  const totalCount = pagination?.total_count ?? workers.length;
  const totalPages = pagination?.total_pages ?? 1;
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = totalCount === 0 ? 0 : Math.min(page * PAGE_SIZE, totalCount);

  if (loading) {
    return (
      <AdminLayout title="Staff Management" subtitle="Loading…">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Staff Management">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Staff Management"
      subtitle="View and manage all active staff members."
      actions={
        <div className="flex items-center gap-2">
          {selectMode && selectedIds.size > 0 && (
            <div className="relative" ref={actionsRef}>
              <button
                onClick={() => setActionsOpen(v => !v)}
                disabled={availabilityLoading}
                aria-haspopup="menu"
                aria-expanded={actionsOpen}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                {availabilityLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                Actions
                <span className="tabular-nums text-xs text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{selectedIds.size}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${actionsOpen ? 'rotate-180' : ''}`} />
              </button>
              {actionsOpen && (
                <div role="menu" className="absolute right-0 mt-1.5 w-60 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-40">
                  <p className="px-3 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    Apply to {selectedIds.size} selected
                  </p>
                  <button role="menuitem" onClick={() => runAction(handleMarkSelectedAvailable)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 text-left">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" /> Mark as Available
                  </button>
                  <button role="menuitem" onClick={() => runAction(() => { setPortalError(null); setPortalConfirm('disable'); })}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 text-left">
                    <ShieldOff className="w-4 h-4 text-amber-500" /> Disable Dashboard Access
                  </button>
                  <button role="menuitem" onClick={() => runAction(() => { setPortalError(null); setPortalConfirm('enable'); })}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 text-left">
                    <ShieldCheck className="w-4 h-4 text-blue-500" /> Enable Dashboard Access
                  </button>
                  <button role="menuitem" onClick={() => runAction(() => { setPublicError(null); setPublicConfirm('show'); })}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 text-left">
                    <Eye className="w-4 h-4 text-blue-500" /> Show on Public Site
                  </button>
                  <button role="menuitem" onClick={() => runAction(() => { setPublicError(null); setPublicConfirm('hide'); })}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-700 text-left">
                    <EyeOff className="w-4 h-4 text-slate-500" /> Hide from Public Site
                  </button>
                  <div className="my-1 border-t border-slate-100" />
                  <button role="menuitem" onClick={() => runAction(openDeleteConfirm)}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 text-left">
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
              )}
            </div>
          )}
          <button
            onClick={() => navigate('/admin/staff-missing-bank-details')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-50 transition-colors"
          >
            <Landmark className="w-4 h-4 text-amber-500" />
            Missing Bank Details
          </button>
          <button
            onClick={toggleSelectMode}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
              selectMode
                ? 'bg-slate-100 border-slate-300 text-slate-700 hover:bg-slate-200'
                : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
            }`}
          >
            {selectMode ? 'Cancel' : 'Select'}
          </button>
          <button
            onClick={() => navigate('/admin/proxy-user-management')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Staff
          </button>
        </div>
      }
    >
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex items-center gap-5 border-b border-slate-200 flex-wrap self-stretch sm:self-auto">
          {STATUS_TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`-mb-px pb-2 pt-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {TAB_LABELS[tab]}
              <span className={`ml-1.5 tabular-nums text-xs ${activeTab === tab ? 'text-blue-500' : 'text-slate-400'}`}>{counts[tab] ?? 0}</span>
            </button>
          ))}
        </div>

        {pendingMigrationCount > 0 && (
          <button
            onClick={() => setPendingMigrationOnly(v => !v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors whitespace-nowrap ${
              pendingMigrationOnly
                ? 'bg-amber-100 border-amber-300 text-amber-800'
                : 'bg-white border-slate-200 text-slate-500 hover:text-slate-700'
            }`}
          >
            Pending Migration <span className="ml-1 tabular-nums">{pendingMigrationCount}</span>
          </button>
        )}

        {/* Dashboard (staff portal) access filter */}
        <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5 self-start sm:self-auto" role="group" aria-label="Dashboard access filter">
          {[
            { key: 'all', label: 'All access', count: null },
            { key: 'enabled', label: 'Has access', count: counts.portal_enabled },
            { key: 'disabled', label: 'No access', count: counts.portal_disabled },
          ].map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setPortalFilter(opt.key)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap transition-colors ${
                portalFilter === opt.key
                  ? (opt.key === 'disabled' ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700')
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {opt.label}
              {opt.count != null && <span className="ml-1 tabular-nums opacity-70">{opt.count}</span>}
            </button>
          ))}
        </div>

        {/* Live-in (at a client's home) willingness filter */}
        <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5 self-start sm:self-auto" role="group" aria-label="Live-in filter">
          {[
            { key: 'all', label: 'Any live-in', count: null },
            { key: 'yes', label: 'Live-in', count: counts.live_in },
            { key: 'no', label: 'Not live-in', count: counts.not_live_in },
          ].map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setLiveInFilter(opt.key)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap transition-colors ${
                liveInFilter === opt.key
                  ? (opt.key === 'no' ? 'bg-slate-200 text-slate-700' : 'bg-indigo-100 text-indigo-700')
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {opt.label}
              {opt.count != null && <span className="ml-1 tabular-nums opacity-70">{opt.count}</span>}
            </button>
          ))}
        </div>

        {/* Hospital-stay willingness filter */}
        <div className="inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5 self-start sm:self-auto" role="group" aria-label="Hospital stay filter">
          {[
            { key: 'all', label: 'Any hospital', count: null },
            { key: 'yes', label: 'Hospital OK', count: counts.hospital },
            { key: 'no', label: 'No hospital', count: counts.not_hospital },
          ].map(opt => (
            <button
              key={opt.key}
              type="button"
              onClick={() => setHospitalFilter(opt.key)}
              className={`px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap transition-colors ${
                hospitalFilter === opt.key
                  ? (opt.key === 'no' ? 'bg-slate-200 text-slate-700' : 'bg-teal-100 text-teal-700')
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {opt.label}
              {opt.count != null && <span className="ml-1 tabular-nums opacity-70">{opt.count}</span>}
            </button>
          ))}
        </div>

        <div className="relative sm:ml-auto">
          <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, phone, code…"
            className="pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-md bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none w-64"
          />
        </div>
      </div>

      {selectMode && selectedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-3 px-4 py-2 bg-blue-50 border border-blue-100 rounded-md text-sm text-blue-800">
          <span className="font-medium tabular-nums">{selectedIds.size} selected</span>
          <span className="text-blue-300">|</span>
          <button onClick={() => setSelectedIds(new Set())} className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900">
            <X className="w-3.5 h-3.5" /> Clear selection
          </button>
          <span className="ml-auto text-xs text-blue-600/80">Use the Actions menu (top right) to apply changes</span>
        </div>
      )}

      {availabilityError && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg">
          {availabilityError}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/80">
                {selectMode && (
                  <th className="px-4 py-3 w-10">
                    {selectAllLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    ) : (
                      <input
                        type="checkbox"
                        checked={totalCount > 0 && selectedIds.size === totalCount}
                        onChange={toggleSelectAll}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    )}
                  </th>
                )}
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Staff</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Gender</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Contact</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                <FilterableTh
                  label="Status"
                  className="text-left px-4 py-2.5 text-[11px]"
                  value={activeTab}
                  onChange={setActiveTab}
                  options={STATUS_TABS.map(tab => ({ key: tab, label: TAB_LABELS[tab], count: counts[tab] ?? 0 }))}
                />
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">Compliance Docs</th>
                <th className="text-left px-4 py-2.5 text-[11px]" aria-sort={sortEarningsDesc ? 'descending' : 'none'}>
                  <button
                    type="button"
                    onClick={() => setSortEarningsDesc(v => !v)}
                    title={sortEarningsDesc ? 'Sorted highest first — click to clear' : 'Sort by highest earnings'}
                    className={`inline-flex items-center gap-1 uppercase tracking-wider font-semibold transition-colors ${
                      sortEarningsDesc ? 'text-blue-600' : 'text-slate-500 hover:text-slate-800'
                    }`}
                  >
                    Current Earnings
                    {sortEarningsDesc
                      ? <ArrowDown className="w-3 h-3" />
                      : <ArrowUpDown className="w-3 h-3 opacity-50" />}
                  </button>
                </th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {workers.length === 0 ? (
                <tr>
                  <td colSpan={selectMode ? 10 : 9} className="text-center py-16 text-slate-400 text-sm">
                    No staff members match your filters.
                  </td>
                </tr>
              ) : workers.map(worker => (
                <tr
                  key={worker.staff_profile_id}
                  onClick={() => selectMode
                    ? toggleSelect(worker.staff_profile_id)
                    : navigate(`/admin/staff/${worker.staff_profile_id}/detail`)}
                  className={`cursor-pointer transition-colors ${selectedIds.has(worker.staff_profile_id) ? 'bg-blue-50/60' : 'hover:bg-slate-50'}`}
                >
                  {/* Select */}
                  {selectMode && (
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(worker.staff_profile_id)}
                        onChange={() => toggleSelect(worker.staff_profile_id)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                    </td>
                  )}

                  {/* Staff */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5 min-w-[180px]">
                      {worker.profile_picture_url ? (
                        <img src={worker.profile_picture_url} alt={worker.full_name}
                          className="w-8 h-8 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-200" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 ring-1 ring-slate-200">
                          <UserCircle className="w-5 h-5 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-blue-600 leading-tight flex items-center gap-1.5">
                          <StaffLink id={worker.staff_profile_id}>{worker.full_name ?? '—'}</StaffLink>
                          {worker.show_on_public_site && (
                            <Eye className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" aria-label="Displayed on the public site" title="Displayed on the public site" />
                          )}
                        </p>
                        {worker.staff_code && (
                          <p className="text-sm text-black font-mono font-bold">{worker.staff_code}</p>
                        )}
                        {(worker.willing_to_live_in || worker.willing_to_stay_at_hospital) && (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {worker.willing_to_live_in && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 px-1.5 py-0.5 rounded">
                                <Home className="w-2.5 h-2.5" /> Live-in
                              </span>
                            )}
                            {worker.willing_to_stay_at_hospital && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-teal-700 bg-teal-50 border border-teal-100 px-1.5 py-0.5 rounded">
                                <Hospital className="w-2.5 h-2.5" /> Hospital OK
                              </span>
                            )}
                          </div>
                        )}
                        {worker.portal_access_disabled && (
                          <span className="inline-flex items-center gap-1 mt-0.5 mr-1 text-[10px] font-semibold text-red-700 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded">
                            <ShieldOff className="w-2.5 h-2.5" /> Dashboard disabled
                          </span>
                        )}
                        {worker.onboarding_status === 'PENDING_MIGRATION' && (
                          <span className="inline-flex items-center gap-1 mt-0.5 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                            Pending Migration
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Gender */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <GenderIcon gender={worker.gender} />
                  </td>

                  {/* Contact */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <p className="text-slate-700">{formatMobileNumber(worker.mobile_number) ?? '—'}</p>
                    {formatMobileNumbers(worker.secondary_phone_numbers).map(({ number, name }) => (
                      <p key={number} className="text-xs text-slate-500">
                        {number}{name && <span className="text-slate-400"> — {name}</span>}
                      </p>
                    ))}
                    <p className="text-xs text-slate-400">{worker.email ?? '—'}</p>
                  </td>

                  {/* Role */}
                  <td className="px-4 py-3">
                    <span className="text-slate-700">{formatRoles(worker.role || worker.designation)}</span>
                  </td>

                  {/* Location */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-slate-600">{worker.location ?? worker.home_address ?? '—'}</span>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusDot status={worker.current_status} />
                  </td>

                  {/* Compliance Docs */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <DocsStatusDot worker={worker} />
                  </td>

                  {/* Current Earnings */}
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="text-slate-700 font-medium">
                      Rs. {parseFloat(worker.current_earnings || 0).toLocaleString('en-IN')}
                    </span>
                  </td>

                  {/* Arrow */}
                  <td className="px-4 py-3 text-right">
                    <ChevronRight className="w-4 h-4 text-slate-300" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {workers.length > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-2.5 text-xs text-slate-400">
            <span>
              Showing {rangeStart}-{rangeEnd} of {totalCount} staff member{totalCount !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={!pagination?.has_prev}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Prev
              </button>
              <span className="px-2 tabular-nums">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={!pagination?.has_next}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-slate-500 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                Next <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Dashboard Access Confirmation Modal */}
      {portalConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => !portalLoading && setPortalConfirm(null)} />
          <div className="relative w-full max-w-sm bg-white rounded-lg shadow-2xl p-5">
            <h2 className="text-sm font-semibold text-slate-900 mb-1.5">
              {portalConfirm === 'disable' ? 'Disable' : 'Enable'} dashboard access for {selectedIds.size} staff member{selectedIds.size !== 1 ? 's' : ''}?
            </h2>
            <p className="text-xs text-slate-500 mb-4">
              {portalConfirm === 'disable'
                ? 'They will lose access to the staff dashboard and can no longer view earnings, bookings or leave. Their account, bookings and payroll are not affected.'
                : 'They will be able to use the staff dashboard again.'}
            </p>
            {portalError && (
              <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md">{portalError}</div>
            )}
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPortalConfirm(null)} disabled={portalLoading}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button type="button" onClick={handlePortalAccessConfirmed} disabled={portalLoading}
                className={`flex-1 px-4 py-2 text-white rounded-md text-sm font-medium disabled:opacity-50 ${portalConfirm === 'disable' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                {portalLoading ? 'Saving…' : portalConfirm === 'disable' ? 'Disable access' : 'Enable access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Public Visibility Confirmation Modal */}
      {publicConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => !publicLoading && setPublicConfirm(null)} />
          <div className="relative w-full max-w-sm bg-white rounded-lg shadow-2xl p-5">
            <h2 className="text-sm font-semibold text-slate-900 mb-1.5">
              {publicConfirm === 'show' ? 'Show' : 'Hide'} {selectedIds.size} staff member{selectedIds.size !== 1 ? 's' : ''} {publicConfirm === 'show' ? 'on' : 'from'} the public site?
            </h2>
            <p className="text-xs text-slate-500 mb-4">
              {publicConfirm === 'show'
                ? 'Their profile will appear on the landing page and the public staff directory, where visitors can view it and book them.'
                : 'Their profile will no longer appear on the landing page or the public staff directory.'}
            </p>
            {publicError && (
              <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md">{publicError}</div>
            )}
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPublicConfirm(null)} disabled={publicLoading}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
                Cancel
              </button>
              <button type="button" onClick={handlePublicVisibilityConfirmed} disabled={publicLoading}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50">
                {publicLoading ? 'Saving…' : publicConfirm === 'show' ? 'Show on site' : 'Hide from site'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={closeDeleteConfirm} />
          <div className="relative w-full max-w-sm bg-white rounded-xl shadow-2xl p-5">
            <h2 className="text-sm font-semibold text-slate-900 mb-1.5">
              Delete {selectedIds.size} staff member{selectedIds.size !== 1 ? 's' : ''}?
            </h2>
            <p className="text-xs text-slate-500 mb-4">
              This permanently deletes the selected staff profile{selectedIds.size !== 1 ? 's' : ''} and login account{selectedIds.size !== 1 ? 's' : ''}.
              This action cannot be undone. Staff with active bookings cannot be deleted.
            </p>

            {deleteError && (
              <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg">
                {deleteError}
              </div>
            )}

            <label className="block text-xs font-medium text-slate-600 mb-1">
              Type <span className="font-semibold text-slate-800">confirm</span> to proceed
            </label>
            <input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="confirm"
              autoFocus
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 bg-white text-slate-800 placeholder-slate-400 transition-colors"
            />

            <div className="flex items-center gap-2 mt-4">
              <button
                type="button"
                onClick={closeDeleteConfirm}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-100 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirmed}
                disabled={deleteLoading || deleteConfirmText.trim().toLowerCase() !== 'confirm'}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

    </AdminLayout>
  );
};

export default StaffManagement;
